import {
  Channels as GraphqlChannel,
  QuestionExpectedAnswer
} from 'generated-graphql/types';
import { Agent, Tone } from 'services/agents/types';
import { Sender } from 'services/conversations/types';
import { User } from 'services/user/types';

export enum CampaignStatus {
  DRAFT = 'DRAFT',
  LIVE = 'LIVE',
  GENERATING_MESSAGES = 'GENERATING_MESSAGES',
  SENDING_MESSAGES = 'SENDING_MESSAGES',
  PAUSED = 'PAUSED',
  FINISHED_SENDING = 'FINISHED_SENDING',
  FINISHED_GENERATING = 'FINISHED_GENERATING',
  SCHEDULED = 'SCHEDULED',
  PROCESSING = 'PROCESSING',
  DISCONNECTED = 'DISCONNECTED',
  ARCHIVED = 'ARCHIVED'
}

export enum ClosingMethod {
  PHONE_NUMBER = 'PHONE_NUMBER',
  MEETING_URL = 'MEETING_URL',
  CALENDAR_MEETING_INTEGRATION = 'CALENDAR_MEETING_INTEGRATION',
  CUSTOM = 'CUSTOM'
}

export enum QuestionTypeOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  GREATER_THAN_OR_EQUAL_TO = 'GREATER_THAN_OR_EQUAL_TO',
  LESS_THAN_OR_EQUAL_TO = 'LESS_THAN_OR_EQUAL_TO'
}

export enum QuestionOutcome {
  SKIP_TO_QUESTION = 'SKIP_TO_QUESTION',
  REJECT = 'REJECT'
}

export type QuestionCondition = {
  nextQuestionIndex?: number;
  operator: QuestionTypeOperator;
  value: string;
  outcome: QuestionOutcome;
};

export type GenerateQuestionsResponse = {
  closedQuestions: {
    content: string;
  }[];
  openEndedQuestions: {
    content: string;
    preferredAnswer: string;
  }[];
};

export type Question = {
  content: string;
  supportingInformation?: string;
  questionType: QuestionType;
  isMandatory?: boolean;
  documentTypes?: string[];
  hasPreferredAnswer?: boolean | null;
  preferredAnswerRequirements?: string[] | null;
  preferredAnswerText?: string | null;
  expectedAnswer?: QuestionExpectedAnswer | null;
  order?: number | null;
};

export type JobDescriptionParsed = {
  jobTitle: string;
  employerName: string;
  location: string;
  summaryOfRole: string;
  contractType?: string | null;
};

export type Campaign = {
  id: string;
  agent?: Agent | null;
  agentId: string;
  userId: string;
  user?: User | null;
  organizationId: string;
  status: CampaignStatus;
  applicationStage?: string | null;
  jobDescriptionParsed?: JobDescriptionParsed | null;
  jobDescriptionText?: string | null;
  additionalContext?: string | null;
  questions: Array<Question> | null;
  meetingUrl?: string | null;
  contactNumber?: string | null;
  customRequest?: string | null;
  csvS3Key?: string | null;
  closingMethod?: ClosingMethod | null;
  closingMessage?: string | null;
  conversationCompletedMessage?: string | null; // Need
  templateId: string;
  candidateBatchId?: string | null;
  totalProspectsCount: number;
  totalConversationsCount: number;
  totalConversationsWithRepliesCount: number;
  initialMessagesSentCount: number;
  totalMessagesCount: number;
  totalMessagesWithMeetingUrlCount: number;
  totalMessagesWithCallRequest: number;
  totalMessagesWithCustomRequest: number;
  totalNotInterestedConversationCount: number;
  totalOptedOutConversationCount: number;
  timeToAutoCloseConversationsInHours?: number | null;
  launchScheduleDateTime?: string | null;
  messagesProcessedCount: number;
  initialMessagesDeliveredCount?: number | null;
  initialMessagesUndeliveredCount?: number | null;
  initialMessagesReadCount?: number | null;
  rejectionMessage?: string | null;
  followUpMessage?: string | null;
  atsJobId?: string | null;
  atsJobPostingId?: string | null;
  webChatTemplateId?: string | null;
  createdAt: string;
  updatedAt: string;
  launchedAt?: string | null;
  type?: CampaignType | null;
  owner?: string | null;
  channel?: GraphqlChannel | null;
  needsReview?: boolean | null;
  title?: string | null;
  outreachDescription?: string | null;
  averageTimeToFirstResponse?: number | null;
  averageTimeToComplete?: number | null;
  atsListId?: string | null;
  customTemplatedMessage?: string | null;
  customTemplatedSubjectLine?: string | null;
  calendarMeetingTemplateId?: string | null;
  externalId?: string | null;
  calendarMeetingTemplate?: {
    id: string;
    title: string;
  } | null;
  isParent?: boolean | null;
  campaignGroupId?: string | null;
  disableNudging?: boolean | null;
  workflowId?: string | null;
};

export type SimulationMessage = {
  id: string;
  body: string;
  createdAt: string;
  updatedAt?: string;
  sender: Sender;
};

export type UpdateCampaignInput = {
  id: string;
  agentId?: string | null;
  title?: string | null;
  userId?: string | null;
  organizationId?: string | null;
  status?: CampaignStatus | null;
  jobDescriptionParsed?: JobDescriptionParsed | null;
  jobDescription?: string | null;
  additionalContext?: string | null;
  questions?: Array<Question> | null;
  meetingUrl?: string | null;
  contactNumber?: string | null;
  customRequest?: string | null;
  csvS3Key?: string | null;
  closingMethod?: ClosingMethod | null;
  closingMessage?: string | null;
  templateId?: string | null;
  candidateBatchId?: string | null;
  totalProspectsCount?: number | null;
  totalConversationsCount?: number | null;
  totalConversationsWithRepliesCount?: number | null;
  initialMessagesSentCount?: number | null;
  totalMessagesCount?: number | null;
  totalMessagesWithMeetingUrlCount?: number | null;
  totalMessagesWithCallRequest?: number | null;
  totalMessagesWithCustomRequest?: number | null;
  totalNotInterestedConversationCount?: number | null;
  totalOptedOutConversationCount?: number | null;
  timeToAutoCloseConversationsInHours?: number | null;
  launchScheduleDateTime?: string | null;
  messagesProcessedCount?: number | null;
  createdAt?: string | null;
  customTemplatedMessage?: string | null;
  customTemplatedSubjectLine?: string | null;
  externalId?: string | null;
  disableNudging?: boolean | null;
};

export type CreateCampaignInput = {
  id?: string | null;
  agentId?: string | null;
  userId: string;
  organizationId: string;
  status: CampaignStatus | null;
  type?: CampaignType | null;
  title?: string | null;
  outreachDescription?: string | null;
  jobDescriptionParsed?: JobDescriptionParsed | null;
  jobDescriptionText?: string | null;
  jobDescription?: string | null;
  additionalContext?: string | null;
  questions?: Array<Question> | null;
  meetingUrl?: string | null;
  contactNumber?: string | null;
  customRequest?: string | null;
  csvS3Key?: string | null;
  closingMethod?: ClosingMethod | null;
  closingMessage?: string | null;
  conversationCompletedMessage?: string | null;
  templateId?: string | null;
  webChatTemplateId?: string | null;
  candidateBatchId?: string | null;
  atsJobId?: string | null;
  atsListId?: string | null;
  atsJobPostingId?: string | null;
  totalProspectsCount?: number | null;
  totalConversationsCount?: number | null;
  totalConversationsWithRepliesCount?: number | null;
  initialMessagesSentCount?: number | null;
  totalMessagesCount?: number | null;
  totalMessagesWithMeetingUrlCount?: number | null;
  totalMessagesWithCallRequest?: number | null;
  totalMessagesWithCustomRequest?: number | null;
  totalNotInterestedConversationCount?: number | null;
  totalOptedOutConversationCount?: number | null;
  initialMessagesDeliveredCount?: number | null;
  initialMessagesUndeliveredCount?: number | null;
  initialMessagesReadCount?: number | null;
  messagesProcessedCount?: number | null;
  timeToAutoCloseConversationsInHours?: number | null;
  launchScheduleDateTime?: string | null;
  followUpMessage?: string | null;
  rejectionMessage?: string | null;
  createdAt?: string | null;
  channel?: GraphqlChannel | null;
  owner?: string | null;
  needsReview?: boolean | null;
  averageTimeToFirstResponse?: number | null;
  averageTimeToComplete?: number | null;
  launchedAt?: string | null;
  customTemplatedMessage?: string | null;
  customTemplatedSubjectLine?: string | null;
  calendarMeetingTemplateId?: string | null;
  disableNudging?: boolean | null;
};

export type CampaignSaverParams = Omit<
  CreateCampaignInput,
  'userId' | 'organizationId' | 'status'
>;

export type JobDescriptionParsedInput = {
  jobTitle: string;
  employerName: string;
  location: string;
  summaryOfRole: string;
  contractType?: string | null;
};

export type InitializeCampaignInput = {
  agentId: string;
  csvS3Key?: string | null;
  meetingUrl?: string | null;
  jobDescriptionParsed?: JobDescriptionParsedInput | null;
  jobDescriptionText?: string | null;
  jobDescription?: string;
  externalId?: string | null;
  additionalContext?: string | null;
  questions?: Array<Question | null> | null;
  userId: string;
  organizationId: string;
  templateId: string;
  closingMethod: ClosingMethod;
  candidateBatchId?: string | null;
  contactNumber?: string | null;
  closingMessage?: string | null;
  customRequest?: string | null;
  timeToAutoCloseConversationsInHours?: number | null;
  type: CampaignType;
  candidatesSelectedIds?: Array<string> | null;
  channel: GraphqlChannel | null | undefined;
  webChatTemplateId: string | null | undefined;
  rejectionMessage: string | null | undefined;
  campaignId: string | null | undefined;
  atsJobId: string | null | undefined;
  atsJobPostingId: string | null | undefined;
  title: string | null | undefined;
  atsListId?: string | null;
  customTemplatedMessage?: string | null;
  customTemplatedSubjectLine?: string | null;
  calendarMeetingTemplateId?: string | null;
  disableNudging?: boolean | null;
};

export type TemplateValuesInput = {
  firstName: string;
  agentName: string;
  companyName: string;
  jobTitle: string;
  employerName: string;
  location: string;
};

export type SendSimulationMessageInput = {
  jobDescription?: string | null;
  jobDescriptionParsed?: JobDescriptionParsedInput | null;
  jobDescriptionText?: string | null;
  additionalContext?: string | null;
  outreachDescription?: string | null;
  questions: Array<Question>;
  messageBody: string;
  agentName: string;
  meetingUrl?: string | null;
  simulationId?: string | null;
  openingMessage: string;
  closingMethod?: ClosingMethod | null;
  contactNumber?: string | null;
  closingMessage?: string | null;
  rejectionMessage?: string | null;
  customRequest?: string | null;
  type: CampaignType;
  tone?: Tone | null;
};

export type CampaignSubscription =
  | {
      id: string;
      campaignId: string;
      userId: string;
      campaign?: Campaign | null;
      user?: User | null;
      createdAt: string;
      updatedAt: string;
    }
  | null
  | undefined;

export type CampaignSubscriptionWithCampaign = {
  id: string;
  campaignId: string;
  userId: string;
  user?: User | null;
  createdAt: string;
  updatedAt: string;
  campaign: Campaign;
};

export enum CampaignType {
  NEW_CANDIDATE_OUTREACH = 'NEW_CANDIDATE_OUTREACH',
  APPLICANT_OUTREACH = 'APPLICANT_OUTREACH',
  ENGAGEMENT_OUTREACH = 'ENGAGEMENT_OUTREACH',
  HUMAN_OUTREACH = 'HUMAN_OUTREACH',
  AVAILABILITY_OUTREACH = 'AVAILABILITY_OUTREACH',
  REQUEST_CV_OUTREACH = 'REQUEST_CV_OUTREACH',
  ATS_JOB_APPLICATION_URL_OUTREACH = 'ATS_JOB_APPLICATION_URL_OUTREACH',
  CUSTOM_JOB_APPLICATION_URL_OUTREACH = 'CUSTOM_JOB_APPLICATION_URL_OUTREACH',
  SCHEDULING = 'SCHEDULING',
  NOTIFICATION_OUTREACH = 'NOTIFICATION_OUTREACH'
}

export enum Channels {
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  WEB_CHAT = 'WEB_CHAT',
  SMS = 'SMS',
  WEB_CHAT_VIA_EMAIL = 'WEB_CHAT_VIA_EMAIL'
}

export enum QuestionType {
  TEXT = 'TEXT',
  DOCUMENT = 'DOCUMENT'
}
